import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button, Icon, toast } from '@/design-system'
import { isAppError } from '@/services/supabase/client'
import { resendConfirmation } from './api'
import { AppMark } from './AppMark'
import './auth.css'

const RESEND_WAIT = 30 // seconds; the server itself enforces 20s per user

/** The landing after signup: the account exists, the email is on its way. */
export function ConfirmEmailScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state ?? {}) as { email?: string; justSent?: boolean }
  const email = state.email?.trim() ?? ''

  const [wait, setWait] = useState(state.justSent ? RESEND_WAIT : 0)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (wait <= 0) return
    const timer = setInterval(() => setWait((s) => s - 1), 1000)
    return () => clearInterval(timer)
  }, [wait > 0])

  async function resend() {
    if (!email || sending || wait > 0) return
    setSending(true)
    try {
      await resendConfirmation(email)
      toast.success('Correo reenviado — mira tu bandeja')
      setWait(RESEND_WAIT)
    } catch (e) {
      toast.error(isAppError(e) ? e.message : 'No se ha podido reenviar el correo.')
      if (isAppError(e) && e.code === 'rate_limited') setWait(20)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="auth">
      <header className="auth__head">
        <AppMark />
        <h1 className="auth__mark">
          Cat<span>GO</span>
        </h1>
        <p className="auth__tagline">Recuerda cada gato que conozcas</p>
      </header>

      <div className="auth__middle">
        <div className="auth__form glass">
          <div className="auth__mailring" aria-hidden="true">
            <Icon name="mail-paw" size={30} strokeWidth={1.6} />
          </div>
          <h2 className="auth__title" style={{ textAlign: 'center' }}>
            Revisa tu correo
          </h2>
          <p className="auth__body" style={{ textAlign: 'center' }}>
            {email ? (
              <>
                Te hemos enviado un correo de CatGO a <strong className="auth__email">{email}</strong>.
                Ábrelo y toca «Confirmar mi cuenta»: te devuelve aquí con todo listo.
              </>
            ) : (
              <>Te hemos enviado un correo de CatGO. Ábrelo y toca «Confirmar mi cuenta» para activar la tuya.</>
            )}
          </p>
          <p className="auth__body auth__spamnote">
            ¿No aparece? Dale un minuto y mira también en correo no deseado.
          </p>

          <div className="auth__actions">
            <Button size="lg" block onClick={() => navigate('/entrar')}>
              Ya lo he confirmado · Entrar
            </Button>
            {email && (
              <Button variant="ghost" size="sm" loading={sending} disabled={wait > 0} onClick={() => void resend()}>
                {wait > 0 ? `Reenviar el correo (${wait}s)` : 'Reenviar el correo'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <footer className="auth__foot">
        <p className="auth__link">
          <Link to="/entrar">Volver a entrar</Link>
        </p>
      </footer>
    </div>
  )
}
